import { ChangeEvent, useEffect } from 'react'

import { Box, FormField, InputSearch } from '@domain-group/suit-theme'
import { useDebounce, useSetState } from '@domain-group/react-hooks'

import ProviderSelector from '@modals/NewOrderModal/components/ProviderSelector'
import ScreenContent from '@modals/NewOrderModal/components/ScreenContent'
import ScreenHeader from '@modals/NewOrderModal/components/ScreenHeader'

import ProviderLogo from '@components/ProviderLogo'
import ServiceIcon from '@components/Icons/ServiceIcon'

import type { UseNewOrderPayload } from '@modals/NewOrderModal'
import type { ProviderPackageModel } from '@models/types'

import type { BuildProviderPackageOrderPayload } from '../../functions/buildProviderPackageOrder'

import ProviderPackage from './components/ProviderPackage'
import type { SetState } from './components/ProviderPackage'

type BuildAndCreateOrderParams = ReturnType<UseNewOrderPayload['callbacks']['buildProviderPackageOrder']> & {
  callbacks: {
    createCampaignQuoteItem: UseNewOrderPayload['callbacks']['createCampaignQuoteItem'],
    createCampaignQuoteTemplateItem: UseNewOrderPayload['callbacks']['createCampaignQuoteTemplateItem'],
    createDefaultQuoteItem: UseNewOrderPayload['callbacks']['createDefaultQuoteItem'],
    createOrder: UseNewOrderPayload['callbacks']['createOrder'],
  },
  itemType: 'quoteItem' | 'quoteTemplateItem',
  payload: BuildProviderPackageOrderPayload['payload'],
}

const buildAndCreateOrder = (params: BuildAndCreateOrderParams) => {
  const {
    callbacks: {
      createCampaignQuoteItem,
      createCampaignQuoteTemplateItem,
      createDefaultQuoteItem,
      createOrder,
    },
    itemType,
    payload,
  } = params

  if (itemType === 'quoteItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteItem(payload)
  }

  if (itemType === 'quoteTemplateItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteTemplateItem(payload)
  }

  if (itemType === 'defaultQuoteItem'){
    // @ts-expect-error TS2345
    return createDefaultQuoteItem(payload)
  }
  // @ts-expect-error TS2345
  return createOrder(payload)
}

interface PackageFlowProps {
  callbacks: {
    addItem: VoidFunction,
    closeModal: VoidFunction,
  },
  newOrderPayload: UseNewOrderPayload,
}

const PackageFlow = (props: PackageFlowProps) => {
  const {
    callbacks: {
      addItem,
      closeModal,
    },
    newOrderPayload: {
      callbacks: {
        buildProviderPackageOrder,
        createOrder,
        createCampaignQuoteItem,
        createCampaignQuoteTemplateItem,
        createDefaultQuoteItem,
        resetState,
        selectProviderId,
        setPackageTitleFilter,
      },
      filteredProviderPackages,
      filteredProvidersWithPackages,
      selectedProvider,
      state: {
        packageTitleFilter,
        selectedProviderId,
      },
    },
  } = props

  const [state, setState] = useSetState({ searchInput: packageTitleFilter || '' })
  const { searchInput } = state

  const debouncedSearchInput = useDebounce(searchInput, 300)

  // Update the filter when debounced value changes
  useEffect(() => {
    setPackageTitleFilter(debouncedSearchInput)
  }, [debouncedSearchInput, setPackageTitleFilter])

  return (
    <>
      <ScreenHeader
        callbacks={{
          goBack: selectedProviderId ? () => selectProviderId(undefined) : () => resetState(),
        }}
        imageComponent={selectedProviderId
          ? <ProviderLogo provider={selectedProvider} />
          : (
            <Box
              alignItems="center"
              borderRadius={5}
              border="1px solid"
              borderColor="lineColor"
              height={50}
              justifyContent="center"
              overflow="hidden"
              width={84}
            >
              <ServiceIcon fillColor="#D0C6B1" height={32} serviceKey="packages" width={32} />
            </Box>
          )}
      >
        <ScreenHeader.Title>
          Packages
        </ScreenHeader.Title>

        <ScreenHeader.Subtitle>
          {selectedProvider.id ? `from ${selectedProvider.title}` : 'Select Provider'}
        </ScreenHeader.Subtitle>
      </ScreenHeader>

      <ScreenContent hasFooter={false}>
        {!selectedProviderId && (
          <ProviderSelector
            callbacks={{ selectProvider: provider => selectProviderId(provider.id) }}
            providers={filteredProvidersWithPackages}
          />
        )}

        {!!selectedProviderId && (
          <>
            <FormField label="Filter Packages">
              {/*
               // @ts-expect-error TS2741 */}
              <InputSearch
                onChange={(e: ChangeEvent<HTMLInputElement>) => setState({ searchInput: e.target.value })}
                value={searchInput}
              />
            </FormField>

            {filteredProviderPackages.map((providerPackage: ProviderPackageModel) => (
              <ProviderPackage
                callbacks={{
                  selectPackage: (setButtonState: SetState) => {
                    const addItemPayload = {
                      callbacks: {
                        action: (
                          actionParams: ReturnType<UseNewOrderPayload['callbacks']['buildProviderPackageOrder']>,
                        ) => (
                          // @ts-expect-error TS2345
                          buildAndCreateOrder({
                            ...actionParams,
                            callbacks: {
                              createOrder,
                              createCampaignQuoteItem,
                              createCampaignQuoteTemplateItem,
                              createDefaultQuoteItem,
                            },
                          })
                        ),
                        afterAction: () => {
                          setButtonState({ loading: false })
                          return closeModal()
                        },
                        afterError: () => setButtonState({ loading: false }),
                        buildActionParams: () => {
                          const buildProviderPackageOrderPayload = buildProviderPackageOrder({
                            providerPackageId: providerPackage.id,
                          })

                          return [buildProviderPackageOrderPayload]
                        },
                      },
                      toastText: 'Item Created',
                    }

                    // @ts-expect-error TS2554
                    addItem(addItemPayload)
                  },
                }}
                key={providerPackage.id}
                providerPackage={providerPackage}
              />
            ))}
          </>
        )}
      </ScreenContent>
    </>
  )
}

export default PackageFlow
